import React, { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { HtmlDocumentViewer } from '@/components/HtmlDocumentViewer';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Plus, Trash2, Download, Eye, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface ExperienceItem {
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  responsibilities: string[];
}

interface EducationItem {
  degree: string;
  school: string;
  location: string;
  graduationDate: string;
}

const DocumentGenerator = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [generatedDocUrl, setGeneratedDocUrl] = useState('');
  const [documentData, setDocumentData] = useState<any>(null);
  const [documentTitle, setDocumentTitle] = useState('My_Resume');

  // Personal Info
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    phone: '',
    location: ''
  });

  // Summary
  const [summary, setSummary] = useState('');

  // Experience
  const [experience, setExperience] = useState<ExperienceItem[]>([
    {
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      responsibilities: ['']
    }
  ]);

  // Education
  const [education, setEducation] = useState<EducationItem[]>([
    {
      degree: '',
      school: '',
      location: '',
      graduationDate: ''
    }
  ]);

  // Skills
  const [skills, setSkills] = useState<string[]>(['']);

  const addExperience = () => {
    setExperience([...experience, {
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      responsibilities: ['']
    }]);
  };

  const removeExperience = (index: number) => {
    setExperience(experience.filter((_, i) => i !== index));
  };

  const updateExperience = (index: number, field: keyof ExperienceItem, value: any) => {
    const updated = [...experience];
    updated[index] = { ...updated[index], [field]: value };
    setExperience(updated);
  };

  const addResponsibility = (expIndex: number) => {
    const updated = [...experience];
    updated[expIndex].responsibilities.push('');
    setExperience(updated);
  };

  const updateResponsibility = (expIndex: number, respIndex: number, value: string) => {
    const updated = [...experience];
    updated[expIndex].responsibilities[respIndex] = value;
    setExperience(updated);
  };

  const removeResponsibility = (expIndex: number, respIndex: number) => {
    const updated = [...experience];
    updated[expIndex].responsibilities = updated[expIndex].responsibilities.filter((_, i) => i !== respIndex);
    setExperience(updated);
  };

  const addEducation = () => {
    setEducation([...education, {
      degree: '',
      school: '',
      location: '',
      graduationDate: ''
    }]);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const updateEducation = (index: number, field: keyof EducationItem, value: string) => {
    const updated = [...education];
    updated[index] = { ...updated[index], [field]: value };
    setEducation(updated);
  };

  const addSkill = () => {
    setSkills([...skills, '']);
  };

  const updateSkill = (index: number, value: string) => {
    const updated = [...skills];
    updated[index] = value;
    setSkills(updated);
  };

  const removeSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const generateDocument = async () => {
    setLoading(true);
    try {
      const contentData = {
        personalInfo,
        summary,
        experience: experience.filter(exp => exp.title || exp.company),
        education: education.filter(edu => edu.degree || edu.school),
        skills: skills.filter(skill => skill.trim() !== '')
      };

      const { data, error } = await supabase.functions.invoke('generate-docx-document', {
        body: {
          title: documentTitle,
          content: contentData
        }
      });

      if (error) throw error;

      if (data.success) {
        setGeneratedDocUrl(data.publicUrl);
        setDocumentData(contentData);
        toast({
          title: "Document Generated!",
          description: `${data.filename} is ready for viewing and download`,
        });
      } else {
        throw new Error(data.error || 'Failed to generate document');
      }

    } catch (error: any) {
      console.error('Generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate document",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (generatedDocUrl) {
      const link = document.createElement('a');
      link.href = generatedDocUrl;
      link.download = `${documentTitle}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Word Document Generator</h1>
          <p className="text-muted-foreground">
            Create professional Word documents (.docx) on the fly with instant preview and download
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Form Panel */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Document Builder
              </h2>

              {/* Document Title */}
              <div className="mb-6">
                <Label htmlFor="docTitle">Document Title</Label>
                <Input
                  id="docTitle"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="My_Resume"
                />
              </div>

              {/* Personal Information */}
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-lg">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={personalInfo.name}
                      onChange={(e) => setPersonalInfo({...personalInfo, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={personalInfo.email}
                      onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={personalInfo.phone}
                      onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={personalInfo.location}
                      onChange={(e) => setPersonalInfo({...personalInfo, location: e.target.value})}
                      placeholder="City, State"
                    />
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              {/* Professional Summary */}
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-lg">Professional Summary</h3>
                <Textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="A brief professional summary highlighting your key qualifications..."
                  rows={4}
                />
              </div>

              <Separator className="my-6" />

              {/* Experience */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Professional Experience</h3>
                  <Button size="sm" onClick={addExperience}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Experience
                  </Button>
                </div>

                {experience.map((exp, expIndex) => (
                  <Card key={expIndex} className="p-4 border-l-4 border-l-primary">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium">Experience {expIndex + 1}</h4>
                      {experience.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeExperience(expIndex)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Job Title</Label>
                        <Input
                          value={exp.title}
                          onChange={(e) => updateExperience(expIndex, 'title', e.target.value)}
                          placeholder="Software Engineer"
                        />
                      </div>
                      <div>
                        <Label>Company</Label>
                        <Input
                          value={exp.company}
                          onChange={(e) => updateExperience(expIndex, 'company', e.target.value)}
                          placeholder="Tech Corp"
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={exp.location}
                          onChange={(e) => updateExperience(expIndex, 'location', e.target.value)}
                          placeholder="San Francisco, CA"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Start Date</Label>
                          <Input
                            value={exp.startDate}
                            onChange={(e) => updateExperience(expIndex, 'startDate', e.target.value)}
                            placeholder="Jan 2022"
                          />
                        </div>
                        <div>
                          <Label>End Date</Label>
                          <Input
                            value={exp.endDate}
                            onChange={(e) => updateExperience(expIndex, 'endDate', e.target.value)}
                            placeholder="Present"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Responsibilities</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addResponsibility(expIndex)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add
                        </Button>
                      </div>
                      {exp.responsibilities.map((resp, respIndex) => (
                        <div key={respIndex} className="flex gap-2 mb-2">
                          <Input
                            value={resp}
                            onChange={(e) => updateResponsibility(expIndex, respIndex, e.target.value)}
                            placeholder="Describe your key accomplishment or responsibility..."
                            className="flex-1"
                          />
                          {exp.responsibilities.length > 1 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeResponsibility(expIndex, respIndex)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>

              <Separator className="my-6" />

              {/* Education */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Education</h3>
                  <Button size="sm" onClick={addEducation}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Education
                  </Button>
                </div>

                {education.map((edu, index) => (
                  <Card key={index} className="p-4 border-l-4 border-l-secondary">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium">Education {index + 1}</h4>
                      {education.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeEducation(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Degree</Label>
                        <Input
                          value={edu.degree}
                          onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                          placeholder="Bachelor of Science in Computer Science"
                        />
                      </div>
                      <div>
                        <Label>School</Label>
                        <Input
                          value={edu.school}
                          onChange={(e) => updateEducation(index, 'school', e.target.value)}
                          placeholder="University of Technology"
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={edu.location}
                          onChange={(e) => updateEducation(index, 'location', e.target.value)}
                          placeholder="Boston, MA"
                        />
                      </div>
                      <div>
                        <Label>Graduation Date</Label>
                        <Input
                          value={edu.graduationDate}
                          onChange={(e) => updateEducation(index, 'graduationDate', e.target.value)}
                          placeholder="May 2020"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <Separator className="my-6" />

              {/* Skills */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Skills</h3>
                  <Button size="sm" onClick={addSkill}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Skill
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {skills.map((skill, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={skill}
                        onChange={(e) => updateSkill(index, e.target.value)}
                        placeholder="JavaScript, React, Node.js"
                        className="flex-1"
                      />
                      {skills.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeSkill(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <div className="flex gap-4">
                <Button
                  onClick={generateDocument}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate .docx
                    </>
                  )}
                </Button>

                {documentData && (
                  <Button onClick={handleDownload} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Preview Panel */}
          <div>
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Document Preview
              </h2>
              
              {documentData ? (
                <HtmlDocumentViewer 
                  documentData={documentData}
                  downloadUrl={generatedDocUrl}
                  fileName={`${documentTitle}.docx`}
                  height="700px"
                />
              ) : (
                <div className="h-96 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-center max-w-md">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">
                      Fill out the form and click "Generate .docx" to create your document
                    </p>
                    <div className="text-xs text-muted-foreground text-left space-y-1">
                      <p><strong>What you'll get:</strong></p>
                      <p>• Native .docx file ready for Microsoft Word</p>
                      <p>• Professional formatting and layout</p>
                      <p>• Instant preview with Office Online</p>
                      <p>• Direct download capability</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Info Section */}
        <Card className="mt-6 p-6">
          <h3 className="text-lg font-semibold mb-3">How Document Generation Works</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-muted-foreground">
            <div>
              <h4 className="font-medium text-foreground mb-2">1. Create Content</h4>
              <ul className="space-y-1">
                <li>• Fill out your professional information</li>
                <li>• Add multiple experiences and education</li>
                <li>• Customize sections to match your needs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">2. Generate .docx</h4>
              <ul className="space-y-1">
                <li>• Documents created as native Word files</li>
                <li>• Professional formatting applied automatically</li>
                <li>• Stored securely in cloud storage</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">3. Preview & Download</h4>
              <ul className="space-y-1">
                <li>• Instant preview with Office Online</li>
                <li>• Direct download for editing in Word</li>
                <li>• Full compatibility with Microsoft Office</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};

export default DocumentGenerator;